import { useState, useEffect, useCallback, useMemo } from "react";
import { 
  Calendar, 
  CalendarDays, 
  Mail, 
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Eye,
  Plus,
  Edit,
  Users,
  User,
  LayoutDashboard,
  Upload,
  Image as ImageIcon,
  Images,
  Ticket,
  Building,
  Settings,
  Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminTicketBookings({ credentials, userRole }) {
  const [ticketBookings, setTicketBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  
  const canModify = userRole === 'admin';

  const headers = useMemo(() => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${auth}` };
  }, [credentials.username, credentials.password]);

  const fetchTicketBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ticket-bookings`, { headers });
      setTicketBookings(response.data);
    } catch (error) {
      console.error("Error fetching ticket bookings:", error);
      toast.error("Failed to load ticket bookings");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchTicketBookings();
  }, [fetchTicketBookings]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/ticket-bookings/${id}`, { status }, { headers });
      toast.success(`Booking ${status}`);
      fetchTicketBookings();
    } catch (error) {
      toast.error("Failed to update booking");
    }
  };

  const deleteBooking = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/ticket-bookings/${deleteId}`, { headers });
      toast.success("Ticket booking deleted");
      setDeleteId(null);
      fetchTicketBookings();
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "status-pending",
      confirmed: "status-available",
      paid: "bg-[#0369A1] text-white",
      cancelled: "status-booked"
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div data-testid="admin-ticket-bookings">
      <div className="flex justify-between items-center mb-8">
        <h1 className="heading-lg">Ticket Bookings</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : ticketBookings.length === 0 ? (
        <Card className="card-heritage">
          <CardContent className="p-12 text-center">
            <Ticket className="w-12 h-12 text-[#D6D3D1] mx-auto mb-4" />
            <p className="text-[#78716C]">No ticket bookings yet</p>
            <p className="text-sm text-[#A8A29E] mt-2">Ticket bookings will appear here when customers book event tickets</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {ticketBookings.map((booking) => (
            <Card key={booking.id} className="card-heritage" data-testid={`ticket-booking-${booking.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="font-semibold text-[#1C1917]">{booking.name}</h3>
                      {getStatusBadge(booking.status)}
                    </div>
                    <p className="text-sm text-[#78716C] mb-2">
                      <strong>{booking.event_title}</strong> - {booking.event_date}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-[#78716C]">
                      <p><Mail className="w-4 h-4 inline mr-1" /> {booking.email}</p>
                      <p><Users className="w-4 h-4 inline mr-1" /> Adults: {booking.adult_tickets}, Children: {booking.child_tickets}</p>
                      <p><Ticket className="w-4 h-4 inline mr-1" /> Total: £{booking.total_amount?.toFixed(2) || "0.00"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {booking.status === "pending" && (
                      <>
                        <Button 
                          size="sm" 
                          className="bg-[#15803D] hover:bg-[#166534]"
                          onClick={() => updateStatus(booking.id, "confirmed")}
                        >
                          <CheckCircle size={16} className="mr-1" /> Confirm
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => updateStatus(booking.id, "cancelled")}
                        >
                          <XCircle size={16} className="mr-1" /> Cancel
                        </Button>
                      </>
                    )}
                    {booking.status === "confirmed" && (
                      <Button 
                        size="sm" 
                        className="bg-[#0369A1] hover:bg-[#075985]"
                        onClick={() => updateStatus(booking.id, "paid")}
                      >
                        Mark Paid
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-[#B91C1C]"
                      onClick={() => setDeleteId(booking.id)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ticket Booking?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the ticket booking.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteBooking} className="bg-[#B91C1C] hover:bg-[#991B1B]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Internal Booking Component - Simplified booking form for admin use
